import { FieldConfig } from "@/types/fieldConfig";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { Card, CardFooter, CardHeader, Chip } from "@heroui/react";
import dayjs from "dayjs";
import {
    getPlotStatusChip,
    getPlantingSource,
} from "@/components/home/FieldConfigCells";

type MobileFieldConfigCardProps = {
    field: FieldConfig;
    growersById: Record<number, Organization>;
    serviceProvidersById: Record<number, Organization>;
    accountManagersById: Record<number, User>;
    isConsultantOrSentinel: boolean;
    userIsSentinel: boolean;
    onPress?: () => void;
};

export default function MobileFieldConfigCard({
    field,
    growersById,
    serviceProvidersById,
    accountManagersById,
    isConsultantOrSentinel,
    userIsSentinel,
    onPress,
}: MobileFieldConfigCardProps) {
    const growerName = growersById[field.grower]?.name;
    const serviceProviderName =
        serviceProvidersById[field.service_provider]?.name;
    const accountManagerName =
        accountManagersById[field.account_manager]?.full_name;

    const plotStatus = getPlotStatusChip(field);

    const cropName =
        field.active_planting_operation?.crop_configuration?.crop_name;

    const plantingDate = field.active_planting_operation?.start_date;

    const plantingIsLogged = getPlantingSource(field) === "Logged";

    return (
        <Card
            isPressable={!!onPress}
            onPress={onPress}
            className="w-full hover:bg-gray-200"
            shadow="sm"
        >
            <CardHeader className="pb-1 px-3">
                <div className="flex w-full items-start justify-between gap-2 text-left">
                    <div className="flex flex-1 flex-col items-start min-w-0 text-left">
                        <p className="w-full text-left font-semibold text-lg truncate">
                            {field.name}
                        </p>

                        {isConsultantOrSentinel && growerName && (
                            <p className="w-full text-left text-sm truncate">
                                {growerName}
                            </p>
                        )}
                    </div>

                    <Chip
                        color={plotStatus.color}
                        size="sm"
                        radius="sm"
                        className="shrink-0 max-w-[50%]"
                        variant="flat"
                    >
                        <span className="truncate">{plotStatus.text}</span>
                    </Chip>
                </div>
            </CardHeader>

            <CardFooter className="text-xs pt-0">
                <div className="flex flex-row justify-between w-full items-end gap-2">
                    <div className="flex gap-1 flex-wrap min-w-0">
                        {cropName && (
                            <Chip
                                classNames={{
                                    base: "bg-gray-100",
                                    content: "text-gray-900 font-bold",
                                }}
                                size="sm"
                                radius="sm"
                            >
                                {cropName}
                            </Chip>
                        )}

                        {plantingDate && (
                            <Chip
                                color={plantingIsLogged ? "success" : "default"}
                                variant="flat"
                                classNames={{
                                    content: "font-bold",
                                }}
                                size="sm"
                                radius="sm"
                            >
                                {dayjs(plantingDate).format("MMM D")}
                            </Chip>
                        )}
                    </div>

                    {userIsSentinel && serviceProviderName && (
                        <div className="text-right shrink-0 max-w-[55%] truncate">
                            {serviceProviderName}
                            {accountManagerName && <> ({accountManagerName})</>}
                        </div>
                    )}
                </div>
            </CardFooter>
        </Card>
    );
}
